import {
  announcementSchema,
  padrinhoSummarySchema,
  type Announcement,
  type PadrinhoSummary,
} from '@growup/streaming-shared';
import { Button, Card, Field, Input, Notice, Select, Textarea } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';

/**
 * Anúncios da equipa de streaming (GS12). The Stream Staff (`staff`) writes to
 * the whole team or to one padrinho's afilhados and can delete; a padrinho
 * (`padrinho`) writes only to their own afilhados. Every streamer in the
 * audience gets an email and sees it on their panel.
 */
export function AnnouncementsView({ mode }: { mode: 'staff' | 'padrinho' }) {
  const base =
    mode === 'staff' ? '/api/streaming/announcements' : '/api/streaming/padrinho/announcements';
  const [list, setList] = useState<Announcement[] | null>(null);
  const [padrinhos, setPadrinhos] = useState<PadrinhoSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    getJson(base, z.array(announcementSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [base]);
  useEffect(load, [load]);
  useEffect(() => {
    if (mode !== 'staff') return;
    getJson('/api/streaming/padrinhos', z.array(padrinhoSummarySchema))
      .then(setPadrinhos)
      .catch(() => setPadrinhos([]));
  }, [mode]);

  const remove = async (id: string) => {
    setError(null);
    try {
      await send('DELETE', `/api/streaming/announcements/${id}`);
      load();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Composer
        mode={mode}
        base={base}
        padrinhos={padrinhos}
        onPublished={(a) => setList((l) => [a, ...(l ?? [])])}
      />
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {list && list.length === 0 ? (
        <p className="text-gu-ink-muted">Ainda não há anúncios.</p>
      ) : null}
      {list?.map((a) => (
        <Card
          key={a.id}
          headingLevel={2}
          title={a.title}
          eyebrow={`${a.padrinho ? `Afilhados de ${a.padrinho.login}` : 'Toda a equipa'} · ${a.author} · ${formatDateTime(a.createdAt)}`}
          actions={
            mode === 'staff' ? (
              <Button variant="ghost" size="sm" onClick={() => void remove(a.id)}>
                Apagar
              </Button>
            ) : undefined
          }
        >
          <p className="whitespace-pre-line">{a.body}</p>
        </Card>
      ))}
    </div>
  );
}

function Composer({
  mode,
  base,
  padrinhos,
  onPublished,
}: {
  mode: 'staff' | 'padrinho';
  base: string;
  padrinhos: PadrinhoSummary[];
  onPublished: (a: Announcement) => void;
}) {
  const [audience, setAudience] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);

  const publish = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const published = announcementSchema.parse(
        await send('POST', base, {
          title,
          body,
          ...(mode === 'staff' && audience !== '' ? { padrinhoId: audience } : {}),
        }),
      );
      onPublished(published);
      setTitle('');
      setBody('');
      setMessage({ tone: 'success', text: 'Publicado. Os streamers receberam um email.' });
    } catch (e) {
      setMessage({ tone: 'danger', text: messageOf(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      headingLevel={2}
      title="Novo anúncio"
      eyebrow={
        mode === 'staff'
          ? 'Cada streamer recebe um email e vê-o no painel.'
          : 'Para os seus afilhados: cada um recebe um email e vê-o no painel.'
      }
    >
      <div className="flex flex-col gap-3">
        {mode === 'staff' ? (
          <Field label="Para" className="max-w-sm">
            <Select value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="">Toda a equipa</option>
              {padrinhos.map((p) => (
                <option key={p.accountId} value={p.accountId}>
                  Afilhados de {p.login}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Título">
          <Input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Texto">
          <Textarea
            value={body}
            rows={5}
            maxLength={5000}
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
        {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
        <div>
          <Button
            size="sm"
            disabled={busy || title.trim().length < 3 || body.trim() === ''}
            onClick={() => void publish()}
          >
            Publicar
          </Button>
        </div>
      </div>
    </Card>
  );
}
